import { Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { isDefined } from "@mui/x-charts/internals";
import type { Test } from "../../../../types";

interface AnalysisTabProps {
  test: Test | undefined;
}

export function AnalysisTab({ test }: AnalysisTabProps) {
  return (
    <Grid container spacing={{ xs: 2, md: 3 }}>
      <Grid size={{ xs: 6, md: 3 }}>
        <Card sx={{ height: "180px" }}>
          <CardContent>
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ height: "100%" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                PFS Score
              </Typography>
              <Typography
                variant="h3"
                component="div"
                color={
                  isDefined(test?.pfs_score) && test.pfs_score < 0.05
                    ? "success.main"
                    : isDefined(test?.pfs_score) && test.pfs_score < 0.15
                      ? "warning.main"
                      : "error.main"
                }
              >
                {isDefined(test?.pfs_score) ? `${(test.pfs_score * 100).toFixed(1)}%` : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The probability of failure in good state, i.e. Probabilistic Flakiness Score. Lower
                is better.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <Card sx={{ height: "180px" }}>
          <CardContent>
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ height: "100%" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Failure Probability
              </Typography>
              <Typography variant="h3" component="div">
                {isDefined(test?.pfs_bad_state_prob)
                  ? `${(test.pfs_bad_state_prob * 100).toFixed(1)}%`
                  : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The legit probability of failure in bad state.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <Card sx={{ height: "180px" }}>
          <CardContent>
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ height: "100%" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Runs analysed
              </Typography>
              <Typography variant="h3" component="div">
                {isDefined(test?.pfs_num_observations)
                  ? test.pfs_num_observations.toFixed(0)
                  : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The number of runs analysed for this test.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <Card sx={{ height: "180px" }}>
          <CardContent>
            <Stack
              direction="column"
              spacing={1}
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ height: "100%" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Pass Probability
              </Typography>
              <Typography variant="h3" component="div">
                {isDefined(test?.pfs_score) && isDefined(test.pfs_bad_state_prob)
                  ? // P(fail then pass) = pb * pf
                    `${((1 - (test.pfs_bad_state_prob + test.pfs_score)) * 100).toFixed(1)}%`
                  : "N/A"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The probability of the test to pass at the first attempt.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
